import { useMemo, useState } from 'preact/hooks'
import { useToast } from '@/hooks/useToast'
import { useCrmPickerOptions } from '@/hooks/useCrmPickerOptions'
import type { MutationResult } from '@/hooks/useCrmSuppressionSources'
import type {
  CrmSuppressionGrain,
  CrmSuppressionProvider,
  CrmSuppressionReason,
  CrmSuppressionSourceCreatePayload,
  CrmSuppressionSourceKind,
} from '@/types/crmSuppressionSources'
import {
  CRM_SUPPRESSION_REASONS,
  HUBSPOT_KINDS,
  SALESFORCE_KINDS,
  emptyPickerMessage,
  pickerLabel,
  pickerNoun,
} from './crmSuppressionReasons'

interface Props {
  clientCompanyId: string
  provider: CrmSuppressionProvider
  onClose: () => void
  onSave: (payload: CrmSuppressionSourceCreatePayload) => Promise<MutationResult>
}

/**
 * The add-source form: pick what to read, then declare what it means. Picker loading lives in
 * useCrmPickerOptions and the reason copy in crmSuppressionReasons, so this file is the form itself.
 */
export function AddCrmSuppressionSourceModal({ clientCompanyId, provider, onClose, onSave }: Props) {
  const kinds = provider === 'HubSpot' ? HUBSPOT_KINDS : SALESFORCE_KINDS
  const [kind, setKind] = useState<CrmSuppressionSourceKind>(kinds[0])
  const [grain, setGrain] = useState<CrmSuppressionGrain>('Individual')
  const [reason, setReason] = useState<CrmSuppressionReason>('Unsubscribed')
  const [externalId, setExternalId] = useState('')
  const [externalValue, setExternalValue] = useState('')
  const [saving, setSaving] = useState(false)
  const { showSuccess, showError } = useToast()

  const { options, loading: loadingOptions, needsPicker, error: optionsError, permissionDenied } = useCrmPickerOptions(clientCompanyId, kind)
  const isFieldCondition = kind === 'Salesforce Field Condition'

  const selected = useMemo(() => options.find(option => option.Id === externalId), [options, externalId])
  const activeReason = CRM_SUPPRESSION_REASONS.find(option => option.value === reason)
  // A picklist without a value would save, then fail every night — block it in the form as well as
  // on the server, so the client finds out now rather than from a status badge tomorrow.
  const needsMatchValue = isFieldCondition && selected?.Detail === 'picklist'
  const canSave = !saving
    && (!needsPicker || externalId !== '')
    && (!needsMatchValue || externalValue.trim() !== '')

  const changeKind = (next: CrmSuppressionSourceKind) => {
    setKind(next)
    // The previous pick belongs to a different picker; carrying it over would submit a list id as a
    // field name.
    setExternalId('')
    setExternalValue('')
  }

  const handleSave = async () => {
    setSaving(true)
    const result = await onSave({
      Provider: provider,
      Kind: kind,
      ExternalId: needsPicker ? externalId : undefined,
      ExternalValue: isFieldCondition && externalValue.trim() ? externalValue.trim() : undefined,
      // The picker puts Salesforce's field type in Detail; the API needs it to tell a checkbox (usable
      // with no value) from a picklist (not).
      FieldType: isFieldCondition ? selected?.Detail ?? undefined : undefined,
      // Captured from the picker so the panel can name the source without a live call, and still says
      // something recognisable after the list is renamed or deleted upstream.
      DisplayName: selected?.Name,
      Grain: grain,
      Reason: reason,
    })
    setSaving(false)
    if (result.ok) {
      showSuccess('Suppression source added', 'The first sync is running now.')
      onClose()
    } else if (!result.handled) {
      showError('Could not add the source', result.error ?? 'Please try again.')
    }
  }

  const selectClass =
    'mt-1 w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1.5 text-sm text-slate-900 dark:text-white disabled:opacity-50'
  const labelClass = 'text-xs font-medium text-slate-700 dark:text-slate-300'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-crm-suppression-source-title"
        className="w-full max-w-lg rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-xl"
      >
        <h3 id="add-crm-suppression-source-title" className="text-base font-semibold text-slate-900 dark:text-white">Add a suppression source</h3>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Reading from your connected {provider} account. Nothing is written back to {provider}.
        </p>

        <div className="mt-4 space-y-4">
          <label className="block">
            <span className={labelClass}>What should we read?</span>
            <select
              value={kind}
              onChange={event => changeKind((event.target as HTMLSelectElement).value as CrmSuppressionSourceKind)}
              className={selectClass}
            >
              {kinds.map(option => <option key={option} value={option}>{option}</option>)}
            </select>
          </label>

          {needsPicker && (
            <label className="block">
              <span className={labelClass}>{pickerLabel(kind)}</span>
              <select
                value={externalId}
                disabled={loadingOptions}
                onChange={event => setExternalId((event.target as HTMLSelectElement).value)}
                className={selectClass}
              >
                <option value="">{loadingOptions ? 'Loading…' : 'Choose one…'}</option>
                {options.map(option => (
                  <option key={option.Id} value={option.Id}>
                    {option.Name}{option.Detail ? ` — ${option.Detail}` : ''}
                  </option>
                ))}
              </select>
              {!loadingOptions && optionsError && (
                // A fault, not a refusal. Reconnecting cannot fix a 500, and saying otherwise sends the
                // client round an OAuth loop that leaves them exactly where they started.
                <span className="mt-1 block text-xs text-rose-600 dark:text-rose-400">
                  Could not load {pickerNoun(kind)} from {provider}. {optionsError}
                </span>
              )}
              {!loadingOptions && !optionsError && permissionDenied && (
                // The one outcome reconnecting actually fixes, and now the only one that says so.
                <span className="mt-1 block text-xs text-amber-700 dark:text-amber-300">
                  {provider} would not let us read this. Reconnect {provider} to grant permission, then try again.
                </span>
              )}
              {!loadingOptions && !optionsError && !permissionDenied && options.length === 0 && (
                // Not a fault and not a refusal — the CRM answered and holds none. This used to advise a
                // reconnect, which is the one thing that cannot change the answer, so it read as though
                // the last reconnect had failed.
                <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                  {emptyPickerMessage(kind, provider)}
                </span>
              )}
            </label>
          )}

          {isFieldCondition && (
            <label className="block">
              <span className={labelClass}>
                Match value{needsMatchValue ? '' : ' (optional)'}
              </span>
              <input
                type="text"
                value={externalValue}
                placeholder={needsMatchValue ? 'e.g. Do not email' : 'Leave blank for a checkbox field'}
                onChange={event => setExternalValue((event.target as HTMLInputElement).value)}
                className={selectClass}
              />
              {needsMatchValue && (
                <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                  This is a picklist, so it needs a value to match. Only a checkbox works without one.
                </span>
              )}
            </label>
          )}

          <fieldset>
            <legend className={labelClass}>Who is in it?</legend>
            <div className="mt-1 flex gap-2">
              {(['Individual', 'Company'] as CrmSuppressionGrain[]).map(option => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setGrain(option)}
                  className={`rounded-md px-2.5 py-1.5 text-xs font-medium ring-1 ring-inset ${
                    grain === option
                      ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 ring-transparent'
                      : 'text-slate-600 dark:text-slate-300 ring-slate-300 dark:ring-slate-700'
                  }`}
                >
                  {option === 'Individual' ? 'People' : 'Companies (whole domains)'}
                </button>
              ))}
            </div>
          </fieldset>

          <label className="block">
            <span className={labelClass}>Why are they suppressed?</span>
            <select
              value={reason}
              onChange={event => setReason((event.target as HTMLSelectElement).value as CrmSuppressionReason)}
              className={selectClass}
            >
              {CRM_SUPPRESSION_REASONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            {activeReason && (
              <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">{activeReason.help}</span>
            )}
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => { void handleSave() }}
            disabled={!canSave}
            className="rounded-md bg-slate-900 dark:bg-white px-3 py-1.5 text-sm font-medium text-white dark:text-slate-900 hover:opacity-90 disabled:opacity-50"
          >
            {saving ? 'Adding…' : 'Add source'}
          </button>
        </div>
      </div>
    </div>
  )
}
